import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './index.less';

const LENS_WIDTH = 460;
const LENS_HEIGHT = 220;
const MAGNIFICATION = 2.1;
const BODY_PORTAL_SELECTOR = [
  '.ant-select-dropdown',
  '.ant-dropdown',
  '.ant-picker-dropdown',
  '.ant-cascader-dropdown',
  '.ant-mentions-dropdown',
  '.ant-tooltip',
  '.ant-popover',
].join(', ');
const MAGNIFIER_PORTAL_SELECTOR = '.accessibility-magnifier, .accessibility-magnifier__status';
const DESKTOP_POINTER_QUERY = '(hover: hover) and (pointer: fine)';
const MOBILE_OR_TABLET_USER_AGENT_PATTERN =
  /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet|Silk/i;

type PointerPosition = {
  clientX: number;
  clientY: number;
};

type PortalMirrorPair = {
  source: HTMLElement;
  mirror: HTMLElement;
};

const isIpadLikeDevice = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const { maxTouchPoints = 0, platform = '', userAgent = '' } = navigator;

  return /iPad/i.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1);
};

export const isDesktopMagnifierEnvironment = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  if (isIpadLikeDevice() || MOBILE_OR_TABLET_USER_AGENT_PATTERN.test(navigator.userAgent || '')) {
    return false;
  }

  if (typeof window.matchMedia !== 'function') {
    return true;
  }

  return window.matchMedia(DESKTOP_POINTER_QUERY).matches;
};

export const isMagnifierShortcutEvent = (event: KeyboardEvent) => {
  const key = typeof event.key === 'string' ? event.key.toLowerCase() : '';
  const code = typeof event.code === 'string' ? event.code.toLowerCase() : '';
  const isMKey = key === 'm' || code === 'keym' || event.keyCode === 77;

  return isMKey && event.ctrlKey && !event.altKey && !event.metaKey && !event.shiftKey;
};

const isInsideMagnifierPortal = (node: Node) => {
  const element = node instanceof Element ? node : node.parentElement;

  return Boolean(element?.closest(MAGNIFIER_PORTAL_SELECTOR));
};

const mutationOnlyTouchesMagnifierPortal = (mutation: MutationRecord) => {
  if (isInsideMagnifierPortal(mutation.target)) {
    return true;
  }

  const changedNodes = [...Array.from(mutation.addedNodes), ...Array.from(mutation.removedNodes)];

  return changedNodes.length > 0 && changedNodes.every(isInsideMagnifierPortal);
};

const isVisibleBodyPortal = (element: HTMLElement) => {
  const style = window.getComputedStyle(element);

  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    style.opacity !== '0' &&
    !element.classList.contains('ant-select-dropdown-hidden') &&
    element.getClientRects().length > 0
  );
};

const copyFormControlValues = (sourceRoot: HTMLElement, mirrorRoot: HTMLElement) => {
  const sourceControls = sourceRoot.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >('input, textarea, select');
  const mirrorControls = mirrorRoot.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >('input, textarea, select');

  sourceControls.forEach((sourceControl, index) => {
    const mirrorControl = mirrorControls[index];

    if (!mirrorControl) {
      return;
    }

    mirrorControl.value = sourceControl.value;

    if (sourceControl instanceof HTMLInputElement && mirrorControl instanceof HTMLInputElement) {
      mirrorControl.checked = sourceControl.checked;
    }

    if (sourceControl instanceof HTMLSelectElement && mirrorControl instanceof HTMLSelectElement) {
      mirrorControl.selectedIndex = sourceControl.selectedIndex;
    }
  });
};

const copyCanvasFrames = (sourceRoot: HTMLElement, mirrorRoot: HTMLElement) => {
  const sourceCanvases = sourceRoot.querySelectorAll('canvas');
  const mirrorCanvases = mirrorRoot.querySelectorAll('canvas');

  sourceCanvases.forEach((sourceCanvas, index) => {
    const mirrorCanvas = mirrorCanvases[index];
    const context = mirrorCanvas?.getContext('2d');

    if (!mirrorCanvas || !context) {
      return;
    }

    mirrorCanvas.width = sourceCanvas.width;
    mirrorCanvas.height = sourceCanvas.height;

    try {
      context.drawImage(sourceCanvas, 0, 0);
    } catch {
      // Cross-origin or protected canvases cannot always be copied; the DOM mirror still works.
    }
  });
};

const copyScrollPositions = (sourceRoot: HTMLElement, mirrorRoot: HTMLElement) => {
  mirrorRoot.scrollTop = sourceRoot.scrollTop;
  mirrorRoot.scrollLeft = sourceRoot.scrollLeft;

  const sourceNodes = sourceRoot.querySelectorAll<HTMLElement>('*');
  const mirrorNodes = mirrorRoot.querySelectorAll<HTMLElement>('*');

  sourceNodes.forEach((sourceNode, index) => {
    const mirrorNode = mirrorNodes[index];

    if (!mirrorNode) {
      return;
    }

    mirrorNode.scrollTop = sourceNode.scrollTop;
    mirrorNode.scrollLeft = sourceNode.scrollLeft;
  });
};

const prepareMirrorNode = (mirrorNode: HTMLElement, className?: string) => {
  mirrorNode.removeAttribute('id');
  mirrorNode.setAttribute('aria-hidden', 'true');

  if (className) {
    mirrorNode.classList.add(className);
  }

  mirrorNode.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
};

const prepareMirrorRoot = (mirrorRoot: HTMLElement) => {
  prepareMirrorNode(mirrorRoot, 'accessibility-magnifier__mirror-root');
};

const appendBodyPortals = (sourceRoot: HTMLElement, content: HTMLElement): PortalMirrorPair[] => {
  const portalPairs: PortalMirrorPair[] = [];
  const bodyPortals = document.body.querySelectorAll<HTMLElement>(BODY_PORTAL_SELECTOR);

  bodyPortals.forEach((bodyPortal) => {
    if (
      sourceRoot.contains(bodyPortal) ||
      isInsideMagnifierPortal(bodyPortal) ||
      !isVisibleBodyPortal(bodyPortal)
    ) {
      return;
    }

    const portalMirror = bodyPortal.cloneNode(true) as HTMLElement;

    prepareMirrorNode(portalMirror, 'accessibility-magnifier__mirror-portal');
    copyFormControlValues(bodyPortal, portalMirror);
    copyCanvasFrames(bodyPortal, portalMirror);
    copyScrollPositions(bodyPortal, portalMirror);

    content.appendChild(portalMirror);
    portalPairs.push({ source: bodyPortal, mirror: portalMirror });
  });

  return portalPairs;
};

const AccessibilityMagnifier: React.FC = () => {
  const [active, setActive] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isSupportedEnvironment, setIsSupportedEnvironment] = useState(false);
  const lensRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const mirrorRootRef = useRef<HTMLElement | null>(null);
  const portalMirrorPairsRef = useRef<PortalMirrorPair[]>([]);
  const pointerRef = useRef<PointerPosition>({
    clientX: typeof window === 'undefined' ? 0 : window.innerWidth / 2,
    clientY: typeof window === 'undefined' ? 0 : window.innerHeight / 2,
  });
  const refreshFrameRef = useRef<number | null>(null);
  const scrollFrameRef = useRef<number | null>(null);

  useEffect(() => {
    setMounted(true);

    const updateSupportedEnvironment = () => {
      const isSupported = isDesktopMagnifierEnvironment();

      setIsSupportedEnvironment(isSupported);

      if (!isSupported) {
        setActive(false);
      }
    };

    const pointerMediaQuery =
      typeof window.matchMedia === 'function' ? window.matchMedia(DESKTOP_POINTER_QUERY) : null;

    updateSupportedEnvironment();

    if (pointerMediaQuery?.addEventListener) {
      pointerMediaQuery.addEventListener('change', updateSupportedEnvironment);
    } else {
      pointerMediaQuery?.addListener?.(updateSupportedEnvironment);
    }

    return () => {
      if (pointerMediaQuery?.removeEventListener) {
        pointerMediaQuery.removeEventListener('change', updateSupportedEnvironment);
      } else {
        pointerMediaQuery?.removeListener?.(updateSupportedEnvironment);
      }
    };
  }, []);

  useEffect(() => {
    if (!isSupportedEnvironment) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isMagnifierShortcutEvent(event)) {
        event.preventDefault();
        setActive((currentActive) => !currentActive);
        return;
      }

      if (event.key === 'Escape') {
        setActive(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSupportedEnvironment]);

  const updateLensPosition = useCallback(() => {
    const lens = lensRef.current;
    const content = contentRef.current;

    if (!lens || !content) {
      return;
    }

    const { clientX, clientY } = pointerRef.current;
    const pageX = clientX + window.scrollX;
    const pageY = clientY + window.scrollY;
    const lensOffsetX = LENS_WIDTH / 2;
    const lensOffsetY = LENS_HEIGHT / 2;

    lens.style.transform = `translate3d(${clientX - lensOffsetX}px, ${clientY - lensOffsetY}px, 0)`;
    content.style.transform = `translate3d(${lensOffsetX - pageX * MAGNIFICATION}px, ${
      lensOffsetY - pageY * MAGNIFICATION
    }px, 0) scale(${MAGNIFICATION})`;
  }, []);

  const refreshMirror = useCallback(() => {
    refreshFrameRef.current = null;

    const sourceRoot = document.getElementById('root');
    const content = contentRef.current;

    if (!sourceRoot || !content) {
      return;
    }

    const mirrorRoot = sourceRoot.cloneNode(true) as HTMLElement;

    prepareMirrorRoot(mirrorRoot);
    mirrorRoot.style.width = `${document.documentElement.clientWidth}px`;
    mirrorRoot.style.minHeight = `${Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight,
      window.innerHeight,
    )}px`;

    copyFormControlValues(sourceRoot, mirrorRoot);
    copyCanvasFrames(sourceRoot, mirrorRoot);
    copyScrollPositions(sourceRoot, mirrorRoot);

    content.replaceChildren(mirrorRoot);
    portalMirrorPairsRef.current = appendBodyPortals(sourceRoot, content);
    mirrorRootRef.current = mirrorRoot;
    updateLensPosition();
  }, [updateLensPosition]);

  const scheduleMirrorRefresh = useCallback(() => {
    if (refreshFrameRef.current !== null) {
      return;
    }

    refreshFrameRef.current = window.requestAnimationFrame(refreshMirror);
  }, [refreshMirror]);

  const syncScrollPositions = useCallback(() => {
    scrollFrameRef.current = null;

    const sourceRoot = document.getElementById('root');
    const mirrorRoot = mirrorRootRef.current;

    if (!sourceRoot || !mirrorRoot) {
      return;
    }

    copyScrollPositions(sourceRoot, mirrorRoot);
    portalMirrorPairsRef.current.forEach(({ source, mirror }) => {
      if (source.isConnected && mirror.isConnected) {
        copyScrollPositions(source, mirror);
      }
    });
    updateLensPosition();
  }, [updateLensPosition]);

  const scheduleScrollSync = useCallback(() => {
    if (scrollFrameRef.current !== null) {
      return;
    }

    scrollFrameRef.current = window.requestAnimationFrame(syncScrollPositions);
  }, [syncScrollPositions]);

  useEffect(() => {
    document.body.classList.toggle('accessibility-magnifier-active', active);

    if (!active) {
      mirrorRootRef.current = null;
      portalMirrorPairsRef.current = [];
      contentRef.current?.replaceChildren();
      return () => {
        document.body.classList.remove('accessibility-magnifier-active');
      };
    }

    refreshMirror();

    const observer = new MutationObserver((mutations) => {
      if (!mutations.every(mutationOnlyTouchesMagnifierPortal)) {
        scheduleMirrorRefresh();
      }
    });

    observer.observe(document.body, {
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    });

    const sourceRoot = document.getElementById('root');
    const resizeObserver =
      sourceRoot && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            scheduleMirrorRefresh();
          })
        : null;

    resizeObserver?.observe(sourceRoot as HTMLElement);

    const handlePointerMove = (event: PointerEvent) => {
      pointerRef.current = {
        clientX: event.clientX,
        clientY: event.clientY,
      };
      updateLensPosition();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('scroll', scheduleScrollSync, true);
    window.addEventListener('resize', scheduleMirrorRefresh);

    return () => {
      document.body.classList.remove('accessibility-magnifier-active');
      observer.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('scroll', scheduleScrollSync, true);
      window.removeEventListener('resize', scheduleMirrorRefresh);

      if (refreshFrameRef.current !== null) {
        window.cancelAnimationFrame(refreshFrameRef.current);
        refreshFrameRef.current = null;
      }

      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
        scrollFrameRef.current = null;
      }
    };
  }, [active, refreshMirror, scheduleMirrorRefresh, scheduleScrollSync, updateLensPosition]);

  if (!mounted || !isSupportedEnvironment) {
    return null;
  }

  return createPortal(
    <>
      <span className="accessibility-magnifier__status" aria-live="polite" role="status">
        {active ? 'Magnifier enabled' : 'Magnifier disabled'}
      </span>
      {active && (
        <div className="accessibility-magnifier" ref={lensRef}>
          <div className="accessibility-magnifier__content" ref={contentRef} />
          <div className="accessibility-magnifier__shine" />
        </div>
      )}
    </>,
    document.body,
  );
};

export default AccessibilityMagnifier;
